import { useState, useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Search, ChevronLeft, ChevronRight, X } from 'lucide-react'

import { apiClient } from '../services/api'
import { useOrganization } from '../contexts/OrganizationContext'
import LoadingSpinner from '../components/LoadingSpinner'

// -- Types --

interface CostCenterOption {
  id: string
  code: string
  name: string
}

interface InvoiceSearchResult {
  id: string
  invoice_number: string
  invoice_date: string
  period_start: string | null
  period_end: string | null
  net_amount: number | null
  trip_count: number
  status: string
  billing_group: string | null
  cost_center_codes: string[]
}

interface InvoiceSearchResponse {
  data: InvoiceSearchResult[]
  pagination: {
    page: number
    page_size: number
    total_items: number
    total_pages: number
  }
}

// -- Helpers --

function formatDate(isoStr: string | null): string {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  return d.toLocaleDateString('fi-FI')
}

function formatCurrency(val: number | string | null): string {
  if (val === null || val === undefined || val === '') return ''
  return (
    Number(val).toLocaleString('fi-FI', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' \u20AC'
  )
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0]
}

function thirtyDaysAgoISO(): string {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return d.toISOString().split('T')[0]
}

// Max visible tags before collapsing
const MAX_VISIBLE_TAGS = 8

// -- Component --

export default function InvoicesPage() {
  const { t } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const { selectedOrgId } = useOrganization()

  // Search state
  const [selectedCCIds, setSelectedCCIds] = useState<string[]>([])
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgoISO())
  const [dateTo, setDateTo] = useState(todayISO())
  const [page, setPage] = useState(1)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchTrigger, setSearchTrigger] = useState(0)

  // Cost center search filter
  const [ccSearch, setCcSearch] = useState('')

  // Tag overflow
  const [showAllTags, setShowAllTags] = useState(false)

  const orgParam = selectedOrgId ? `organization_id=${selectedOrgId}` : ''

  // Fetch user's cost centers
  const { data: costCenters } = useQuery<CostCenterOption[]>({
    queryKey: ['invoiceCostCenters', selectedOrgId],
    queryFn: () =>
      apiClient.get(`/invoices/cost-centers${orgParam ? `?${orgParam}` : ''}`),
  })

  // Search invoices (only when triggered)
  const { data: searchResults, isLoading: searchLoading } = useQuery<InvoiceSearchResponse>({
    queryKey: ['invoiceSearch', selectedCCIds, dateFrom, dateTo, page, searchTrigger, selectedOrgId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (selectedCCIds.length > 0) {
        params.set('cost_center_ids', selectedCCIds.join(','))
      }
      params.set('date_from', dateFrom)
      params.set('date_to', dateTo)
      params.set('page', page.toString())
      params.set('page_size', '50')
      if (selectedOrgId) {
        params.set('organization_id', selectedOrgId)
      }
      return apiClient.get(`/invoices/search?${params.toString()}`)
    },
    enabled: hasSearched,
  })

  const handleSearch = useCallback(() => {
    setPage(1)
    setHasSearched(true)
    setSearchTrigger((prev) => prev + 1)
  }, [])

  const handleCCToggle = useCallback((ccId: string) => {
    setSelectedCCIds((prev) =>
      prev.includes(ccId) ? prev.filter((id) => id !== ccId) : [...prev, ccId]
    )
  }, [])

  const handleRemoveCC = useCallback((ccId: string) => {
    setSelectedCCIds((prev) => prev.filter((id) => id !== ccId))
  }, [])

  const handleClearAllCC = useCallback(() => {
    setSelectedCCIds([])
    setShowAllTags(false)
  }, [])

  // Build cost center lookup map (id -> option)
  const ccMap = useMemo(() => {
    const m = new Map<string, CostCenterOption>()
    if (costCenters) {
      for (const cc of costCenters) {
        m.set(cc.id, cc)
      }
    }
    return m
  }, [costCenters])

  // Filter cost centers by search term
  const filteredCostCenters = useMemo(() => {
    if (!costCenters) return []
    if (!ccSearch.trim()) return costCenters
    const term = ccSearch.trim().toLowerCase()
    return costCenters.filter(
      (cc) =>
        cc.code.toLowerCase().includes(term) ||
        cc.name.toLowerCase().includes(term)
    )
  }, [costCenters, ccSearch])

  // Selected cost centers for tag display
  const selectedCCs = useMemo(() => {
    return selectedCCIds
      .map((id) => ccMap.get(id))
      .filter((cc): cc is CostCenterOption => cc != null)
  }, [selectedCCIds, ccMap])

  const visibleTags = showAllTags ? selectedCCs : selectedCCs.slice(0, MAX_VISIBLE_TAGS)
  const hiddenCount = selectedCCs.length - MAX_VISIBLE_TAGS

  const invoices = searchResults?.data || []
  const pagination = searchResults?.pagination

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {tc('dashboard.title')}
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">{t('search.title')}</h1>
      </div>

      {/* Search Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Cost center multi-select with search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('search.costCenters')}
            </label>
            {/* Search field */}
            <div className="relative mb-1">
              <input
                type="text"
                value={ccSearch}
                onChange={(e) => setCcSearch(e.target.value)}
                placeholder={t('search.ccSearchPlaceholder')}
                className="w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-8"
              />
              {ccSearch && (
                <button
                  onClick={() => setCcSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {/* Checkbox list */}
            <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
              {filteredCostCenters.length > 0 ? (
                filteredCostCenters.map((cc) => (
                  <label
                    key={cc.id}
                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCCIds.includes(cc.id)}
                      onChange={() => handleCCToggle(cc.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="font-mono text-xs text-gray-500">{cc.code}</span>
                    <span className="truncate">{cc.name}</span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-gray-400 px-2 py-1">
                  {ccSearch
                    ? t('search.ccSearchNoResults')
                    : t('search.costCenterPlaceholder')}
                </p>
              )}
            </div>
            {/* Selected tags */}
            <div className="mt-2">
              {selectedCCs.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {visibleTags.map((cc) => (
                      <span
                        key={cc.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs"
                      >
                        {cc.code}
                        <button
                          onClick={() => handleRemoveCC(cc.id)}
                          className="text-primary-400 hover:text-primary-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {!showAllTags && hiddenCount > 0 && (
                      <button
                        onClick={() => setShowAllTags(true)}
                        className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                      >
                        +{hiddenCount} {t('search.more')}
                      </button>
                    )}
                  </div>
                  <button
                    onClick={handleClearAllCC}
                    className="text-xs text-gray-500 hover:text-red-600"
                  >
                    {t('search.clearAllCC')}
                  </button>
                </div>
              ) : (
                <p className="text-xs text-gray-400">{t('search.noSelectedCC')}</p>
              )}
            </div>
          </div>

          {/* Date range */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('search.dateFrom')}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('search.dateTo')}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Search button */}
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
            >
              <Search className="w-4 h-4 mr-2" />
              {t('search.searchButton')}
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {!hasSearched && (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center text-gray-500">
          {t('search.hint')}
        </div>
      )}

      {hasSearched && searchLoading && <LoadingSpinner />}

      {hasSearched && !searchLoading && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('search.invoiceNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('search.invoiceDate')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('search.period')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('search.costCentersColumn')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t('search.netAmount')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t('search.tripCount')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      {t('search.noResults')}
                    </td>
                  </tr>
                ) : (
                  invoices.map((inv) => (
                    <tr
                      key={inv.id}
                      onClick={() => navigate(`/invoices/${inv.id}/inspection`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                        {(inv.billing_group || inv.invoice_number).replace(/^B-/, '')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {formatDate(inv.invoice_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {inv.period_start && inv.period_end
                          ? `${formatDate(inv.period_start)} \u2013 ${formatDate(inv.period_end)}`
                          : ''}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {inv.cost_center_codes.length > 0 ? (
                            inv.cost_center_codes.map((code) => (
                              <span
                                key={code}
                                className="inline-block px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-mono"
                              >
                                {code}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400 text-xs">&ndash;</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                        {formatCurrency(inv.net_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                        {inv.trip_count}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
              <div className="text-sm text-gray-600">
                {tc('common.page')} {pagination.page} {tc('common.of')} {pagination.total_pages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                  disabled={page === pagination.total_pages}
                  className="p-2 rounded-lg border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
